import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  ArrowRight,
  Clock,
  DownloadSimple,
  FileText,
  Flame,
  GearSix,
  Headphones,
  MagnifyingGlass,
  Megaphone,
  Microscope,
  Play,
  SealCheck,
  ShareNetwork,
  Sparkle,
  Star,
  TrendUp,
  Wallet,
  X,
  CalendarCheck,
  CurrencyDollar,
  ChatTeardropDots,
  UsersThree,
  Lightning,
  Wrench,
  Plus
} from '@phosphor-icons/react';
import { getWorkerIcon } from '../utils/workerIcons';
import { extractUsedApps, getAppLogo } from '../utils/usedApps';
import { BACKEND_URL } from '../config';

const OUTCOME_ICONS = {
  'Lead & Enquiries': Lightning, 'Bookings & Scheduling': CalendarCheck, 'Quotes & Revenue': CurrencyDollar,
  'Payments & Admin': Wallet, 'Reviews & Reputation': Star, 'Customer Retention': UsersThree,
  'Operations': ChatTeardropDots, 'Advanced': Wrench,
};

const OUTCOME_COLORS = {
  'Lead & Enquiries':       { pill: 'rgba(16,185,129,0.12)', text: '#34D399', glow: 'rgba(16,185,129,0.06)' },
  'Bookings & Scheduling':  { pill: 'rgba(59,130,246,0.12)', text: '#60A5FA', glow: 'rgba(59,130,246,0.06)' },
  'Quotes & Revenue':       { pill: 'rgba(16,185,129,0.12)', text: '#10B981', glow: 'rgba(16,185,129,0.06)' },
  'Payments & Admin':       { pill: 'rgba(250,204,21,0.12)',  text: '#FACC15', glow: 'rgba(250,204,21,0.06)' },
  'Reviews & Reputation':   { pill: 'rgba(245,158,11,0.12)', text: '#F59E0B', glow: 'rgba(245,158,11,0.06)' },
  'Customer Retention':     { pill: 'rgba(139,92,246,0.12)',  text: '#A78BFA', glow: 'rgba(139,92,246,0.06)' },
  'Operations':             { pill: 'rgba(6,182,212,0.12)',   text: '#22D3EE', glow: 'rgba(6,182,212,0.06)' },
  'Advanced':               { pill: 'rgba(100,116,139,0.12)', text: '#94A3B8', glow: 'rgba(100,116,139,0.06)' },
};

const CATEGORY_DISPLAY = {
  'Lead & Enquiries':       { name: 'Capture & Convert Leads', subtext: 'Turn every enquiry into a paying customer' },
  'Bookings & Scheduling':  { name: 'Fill Your Calendar Automatically', subtext: 'Jobs booked without back-and-forth' },
  'Quotes & Revenue':       { name: 'Win More Jobs', subtext: 'Send quotes faster and close more deals' },
  'Payments & Admin':       { name: 'Get Paid Faster (Without Chasing)', subtext: 'Invoices followed up automatically' },
  'Reviews & Reputation':   { name: 'Build Your Reputation', subtext: 'Collect reviews and grow your online presence' },
  'Customer Retention':     { name: 'Keep Customers Coming Back', subtext: 'Nurture relationships and drive repeat business' },
  'Operations':             { name: 'Streamline Operations', subtext: 'Automate the day-to-day so you can focus on growth' },
  'Advanced':               { name: 'Advanced / Power Tools', subtext: 'Specialist AI tools for power users' },
};

const getCatDisplay = (dbName) => CATEGORY_DISPLAY[dbName] || { name: dbName, subtext: '' };

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return n.toLocaleString();
};

/* ── App Icon ── */
const AppIcon = ({ app }) => (
  <img src={app.logo} alt={app.label} title={app.label} loading="lazy"
    className="w-5 h-5 rounded object-contain shrink-0"
    data-testid={`app-icon-${app.slug}`}
    onError={e => { e.target.style.display = 'none'; }}
  />
);

/* ── Workflow Mini-Preview (shows flow nodes from template) ── */
const WorkflowPreview = ({ nodes }) => {
  if (!nodes?.length) return null;
  const display = nodes.filter(n => n.data?.label).slice(0, 3);
  if (!display.length) return null;
  return (
    <div className="flex items-center gap-1.5 overflow-hidden">
      {display.map((node, i) => (
        <React.Fragment key={i}>
          <span className="text-[10px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] px-2 py-0.5 rounded truncate max-w-[120px]">
            {node.data.label}
          </span>
          {i < display.length - 1 && (
            <ArrowRight size={8} className="text-[#4E5563] shrink-0" />
          )}
        </React.Fragment>
      ))}
      {nodes.filter(n => n.data?.label).length > 3 && <span className="text-[10px] text-[#5C6778]">+{nodes.filter(n => n.data?.label).length - 3}</span>}
    </div>
  );
};

/* ── Featured Card (large, with workflow preview + output snippet) ── */
const FeaturedCard = ({ template, onShare }) => {
  const color = OUTCOME_COLORS[template.outcome_category] || OUTCOME_COLORS['Operations'];
  const tools = template.works_with || [];

  return (
    <Link to={`/marketplace/${template.slug}`} data-testid={`featured-card-${template.slug}`} className="group block h-full">
      <div
        className="h-full rounded-2xl border border-[rgba(255,255,255,0.08)] p-7 flex flex-col md:flex-row gap-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(0,0,0,0.4)] hover:border-[rgba(255,255,255,0.12)]"
        style={{ background: `linear-gradient(135deg, #12161C 0%, ${color.glow} 100%)` }}
      >
        {/* Left side: content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <img src={getWorkerIcon(template.outcome_category, template.name)} alt=""
              className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
            <span className="text-[11px] font-medium px-2.5 py-1 rounded-full"
              style={{ backgroundColor: color.pill, color: color.text }}>
              {getCatDisplay(template.outcome_category).name}
            </span>
          </div>
          <h3 className="text-[#F0F4F8] font-bold text-xl leading-tight mb-2 group-hover:text-[#00E5C0] transition-colors">
            {template.name}
          </h3>
          <p className="text-[#8b8fa3] text-sm leading-relaxed mb-4 line-clamp-2">
            {template.short_description || template.description?.replace(/\*\*/g, '').substring(0, 160)}
          </p>
          {tools.length > 0 && (
            <div className="flex items-center gap-1.5 mb-4">
              {tools.slice(0, 5).map((name, i) => (
                <div key={i} className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0" title={name}>
                  <img src={getAppLogo(name)} alt={name} className="w-4 h-4 rounded object-contain" loading="lazy"
                    onError={e => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                </div>
              ))}
              {tools.length > 5 && <span className="text-[10px] text-[#7D8896] ml-0.5">+{tools.length - 5}</span>}
            </div>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#00E5C0] opacity-0 group-hover:opacity-100 transition-opacity">
            View worker <ArrowRight size={11} />
          </span>
        </div>

        {/* Right side: workflow preview */}
        {template.visual_flow_definition?.nodes?.length > 0 && (
          <div className="md:w-[280px] shrink-0 rounded-xl bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.04)] p-4">
            <span className="text-[9px] uppercase tracking-widest text-[#5C6778] font-semibold mb-3 block">Workflow</span>
            <div className="space-y-2">
              {template.visual_flow_definition.nodes.filter(n => n.data?.label).slice(0, 5).map((node, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center text-[9px] font-bold text-[#7D8896] shrink-0">{i + 1}</span>
                  <span className="text-[11px] text-[#B0B8C4] truncate">{node.data.label}</span>
                </div>
              ))}
              {template.visual_flow_definition.nodes.filter(n => n.data?.label).length > 5 && (
                <span className="text-[10px] text-[#5C6778] pl-7">+ {template.visual_flow_definition.nodes.filter(n => n.data?.label).length - 5} more</span>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

/* ── Standard Worker Card (richer design) ── */
const AutomationCard = ({ template, onShare }) => {
  const color = OUTCOME_COLORS[template.outcome_category] || OUTCOME_COLORS['Operations'];
  const tools = template.works_with || [];

  return (
    <Link to={`/marketplace/${template.slug}`} data-testid={`automation-card-${template.slug}`} className="group block h-full">
      <div className="h-full rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1F2937] p-6 flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] hover:border-[rgba(255,255,255,0.1)]">

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <img src={getWorkerIcon(template.outcome_category, template.name)} alt=""
            className="w-11 h-11 rounded-xl object-cover shrink-0" loading="lazy"
            data-testid={`worker-icon-${template.slug}`} />
          <div className="flex-1 min-w-0">
            <h3 className="text-[#F0F4F8] font-semibold text-[15px] leading-snug group-hover:text-[#00E5C0] transition-colors line-clamp-2">
              {template.name}
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-[#7D8896] text-[13px] leading-relaxed line-clamp-2 mb-4">
          {template.short_description || template.description?.replace(/\*\*/g, '').substring(0, 140)}
        </p>

        {/* Tool logos row — max 5, single row, no wrap */}
        {tools.length > 0 && (
          <div className="flex items-center gap-1.5 mb-4 overflow-hidden" data-testid={`card-tools-${template.slug}`}>
            {tools.slice(0, 5).map((name, i) => (
              <div key={i} className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0" title={name}>
                <img src={getAppLogo(name)} alt={name} className="w-4 h-4 rounded object-contain" loading="lazy"
                  onError={e => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
              </div>
            ))}
            {tools.length > 5 && (
              <span className="text-[10px] text-[#7D8896] shrink-0 ml-0.5">+{tools.length - 5}</span>
            )}
          </div>
        )}

        {/* Spacer */}
        <div className="mt-auto" />

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,0.04)]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: color.pill, color: color.text }}>
              {getCatDisplay(template.outcome_category).name}
            </span>
            {template.verification_badge === 'verified' && (
              <SealCheck size={12} className="text-[#60A5FA] shrink-0" weight="fill" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare?.(template); }}
              className="p-1 rounded-md text-[#5C6778] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              data-testid={`share-btn-${template.slug}`} title="Share">
              <ShareNetwork size={14} />
            </button>
            <span className="text-[11px] font-medium text-[#00E5C0] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
              View <ArrowRight size={10} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

/* ── Category Pill ── */
const CategoryPill = ({ name, active, onClick, count }) => {
  const Icon = OUTCOME_ICONS[name] || GearSix;
  const color = OUTCOME_COLORS[name] || OUTCOME_COLORS['Operations'];
  return (
    <button data-testid={`category-pill-${name.toLowerCase().replace(/\s+/g, '-')}`} onClick={onClick}
      className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-150 border"
      style={active ? { backgroundColor: color.pill, color: color.text, borderColor: `${color.text}33` }
        : { backgroundColor: 'rgba(255,255,255,0.03)', color: '#7D8896', borderColor: 'rgba(255,255,255,0.06)' }}>
      <Icon size={13} /> {getCatDisplay(name).name} <span className="opacity-50">({count})</span>
    </button>
  );
};

/* ── Create Your Own Worker Card ── */
const CreateWorkerCard = () => (
  <Link to="/register" data-testid="create-worker-card" className="group block w-full">
    <div className="rounded-2xl border border-dashed border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.03)] p-7 flex items-center gap-6 transition-all duration-300 hover:border-[rgba(0,229,192,0.4)] hover:bg-[rgba(0,229,192,0.06)] hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,229,192,0.08)]">
      <div className="w-14 h-14 rounded-2xl bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center shrink-0">
        <Plus size={24} className="text-[#00E5C0]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[#F0F4F8] font-semibold text-lg mb-1 group-hover:text-[#00E5C0] transition-colors">
          Have something specific? We'll build it for you
        </h3>
        <p className="text-[#7D8896] text-sm">
          Describe your workflow. We'll turn it into a working system.
        </p>
      </div>
      <ArrowRight size={20} className="text-[#00E5C0] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  </Link>
);

/* ── Main Gallery ── */
export default function AutomationsGallery() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('outcome') || null);
  const [activeWorkerType, setActiveWorkerType] = useState(searchParams.get('type') || null);

  const WORKER_TYPE_FILTERS = [
    { id: 'event', label: 'Event' }, { id: 'file_analysis', label: 'File' },
    { id: 'chat', label: 'Chat' }, { id: 'voice', label: 'Voice' }, { id: 'operator', label: 'Operator' },
  ];

  useEffect(() => { fetchAutomations(); }, []);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/public/automations?limit=100`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setTemplates(data.templates || []);
      setGrouped(data.grouped || {});
      setCategories(data.outcome_categories || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const filteredTemplates = useMemo(() => {
    let filtered = templates;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) ||
        t.tags?.some(tag => tag.toLowerCase().includes(q)) ||
        t.marketplace_capabilities?.some(c => c.toLowerCase().includes(q)) ||
        t.marketplace_integrations?.some(i => i.toLowerCase().includes(q))
      );
    }
    if (activeCategory) filtered = filtered.filter(t => t.outcome_category === activeCategory);
    if (activeWorkerType) filtered = filtered.filter(t => t.automation_type === activeWorkerType || t.worker_type === activeWorkerType);
    return filtered;
  }, [templates, searchQuery, activeCategory, activeWorkerType]);

  // Separate specialty workers from main templates
  const specialtyTemplates = useMemo(() => {
    return templates.filter(t => t.is_specialty === true);
  }, [templates]);

  const mainFilteredTemplates = useMemo(() => {
    return filteredTemplates.filter(t => !t.is_specialty);
  }, [filteredTemplates]);

  const filteredGrouped = useMemo(() => {
    const result = {};
    for (const t of mainFilteredTemplates) {
      const cat = t.outcome_category;
      if (!result[cat]) result[cat] = [];
      result[cat].push(t);
    }
    return result;
  }, [mainFilteredTemplates]);

  // Pick featured templates: first 2 from Lead & Enquiries (most impactful for service businesses)
  const featured = useMemo(() => {
    return mainFilteredTemplates.filter(t => t.verification_badge === 'verified').slice(0, 2);
  }, [mainFilteredTemplates]);

  const handleCategoryClick = (cat) => {
    const next = activeCategory === cat ? null : cat;
    setActiveCategory(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set('outcome', next); else params.delete('outcome');
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    const params = new URLSearchParams(searchParams);
    if (e.target.value) params.set('q', e.target.value); else params.delete('q');
    setSearchParams(params, { replace: true });
  };

  const handleShare = (template) => {
    const url = `${window.location.origin}/marketplace/${template.slug}`;
    if (navigator.share) navigator.share({ title: template.name, url });
    else navigator.clipboard.writeText(url);
  };

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const t of mainFilteredTemplates) {
      const cat = t.outcome_category;
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [mainFilteredTemplates]);

  return (
    <>
      <SEO title="AI Worker Marketplace | Man & Machine" description="Browse and install production-ready AI workers." />
      <div className="min-h-screen bg-[#161B26]" data-testid="workers-gallery-page">
        <LandingHeader />

        {/* Hero */}
        <section className="pt-36 pb-12 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F0F4F8] tracking-tight mb-5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Pick What You Want Automated
              </h1>
              <p className="text-lg text-[#7D8896] max-w-2xl mx-auto leading-relaxed">
                Start with proven systems. Each one handles real work in your business.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Search + Filters */}
        <section className="px-6 pb-8">
          <div className="max-w-[1280px] mx-auto">
            <div className="relative mb-6">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6778]" />
              <input data-testid="automations-search-input" type="text" placeholder="Search what you want to automate..."
                value={searchQuery} onChange={handleSearch}
                className="w-full max-w-lg pl-12 pr-10 py-3.5 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] text-sm placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0]/40 transition-all" />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); const p = new URLSearchParams(searchParams); p.delete('q'); setSearchParams(p, { replace: true }); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5C6778] hover:text-[#F0F4F8]"><X size={14} /></button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {categories.map(cat => (
                <CategoryPill key={cat} name={cat} active={activeCategory === cat} onClick={() => handleCategoryClick(cat)} count={categoryCounts[cat] || 0} />
              ))}
              {activeCategory && (
                <button onClick={() => handleCategoryClick(activeCategory)}
                  className="flex items-center gap-1 px-3 py-2 rounded-full text-xs text-[#00E5C0] border border-[rgba(0,229,192,0.2)] hover:bg-[rgba(0,229,192,0.06)] transition-colors">
                  <X size={10} /> Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] text-[#5C6778] uppercase tracking-wider font-medium self-center mr-1">Type:</span>
              {WORKER_TYPE_FILTERS.map(wt => (
                <button key={wt.id} data-testid={`filter-type-${wt.id}`}
                  onClick={() => {
                    const next = activeWorkerType === wt.id ? null : wt.id;
                    setActiveWorkerType(next);
                    const params = new URLSearchParams(searchParams);
                    if (next) params.set('type', next); else params.delete('type');
                    setSearchParams(params, { replace: true });
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                    activeWorkerType === wt.id
                      ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border-[rgba(0,229,192,0.2)]'
                      : 'text-[#5C6778] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)] hover:text-[#B0B8C4]'
                  }`}>{wt.label}</button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Section */}
        {featured.length > 0 && !activeCategory && !searchQuery && (
          <section className="px-6 pb-14">
            <div className="max-w-[1280px] mx-auto">
              <p className="text-xs font-medium text-[#00E5C0] uppercase tracking-wider mb-2">Start here (most popular)</p>
              <h2 className="text-2xl font-bold text-[#F0F4F8] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Featured</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {featured.map(t => <FeaturedCard key={t.slug} template={t} onShare={handleShare} />)}
              </div>
            </div>
          </section>
        )}

        {/* Results */}
        <section className="px-6 pb-24">
          <div className="max-w-[1280px] mx-auto">
            {loading ? (
              <div className="text-center py-20">
                <div className="w-8 h-8 border-2 border-[#00E5C0] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#5C6778] text-sm">Loading workers...</p>
              </div>
            ) : mainFilteredTemplates.length === 0 && !searchQuery ? (
              <div className="text-center py-20" data-testid="automations-empty-state">
                <p className="text-[#7D8896] text-lg mb-2">No workers found</p>
                <p className="text-[#5C6778] text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <>
                {!searchQuery && !activeCategory && (
                  <div className="mb-12">
                    <CreateWorkerCard />
                  </div>
                )}
                {Object.entries(filteredGrouped).map(([category, items]) => {
                  if (category === 'Advanced') return null;
                  const CatIcon = OUTCOME_ICONS[category] || GearSix;
                  const catColor = OUTCOME_COLORS[category] || OUTCOME_COLORS['Operations'];
                  return (
                    <div key={category} className="mb-16" data-testid={`category-section-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="flex items-center gap-3 mb-7">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: catColor.pill }}>
                          <CatIcon size={16} style={{ color: catColor.text }} />
                        </div>
                        <h2 className="text-xl font-bold text-[#F0F4F8]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{getCatDisplay(category).name}</h2>
                        <span className="text-xs text-[#5C6778]">{items.length} worker{items.length !== 1 ? 's' : ''}</span>
                      </div>
                      {getCatDisplay(category).subtext && (
                        <p className="text-sm text-[#5C6778] mb-6 ml-11">{getCatDisplay(category).subtext}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {items.map(t => <AutomationCard key={t.id || t.slug} template={t} onShare={handleShare} />)}
                      </div>
                    </div>
                  );
                })}

                {/* Advanced section: non-specialty "Advanced" + specialty workers */}
                {(() => {
                  const advancedNonSpecialty = (filteredGrouped['Advanced'] || []).filter(t => !t.is_specialty);
                  const showSpecialty = !activeCategory || activeCategory === 'Advanced';
                  const allAdvanced = [...advancedNonSpecialty, ...(showSpecialty ? specialtyTemplates : [])];
                  if (allAdvanced.length === 0) return null;
                  const catColor = OUTCOME_COLORS['Advanced'];
                  const CatIcon = OUTCOME_ICONS['Advanced'] || Wrench;
                  return (
                    <div className="mb-16" data-testid="category-section-advanced">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: catColor.pill }}>
                          <CatIcon size={16} style={{ color: catColor.text }} />
                        </div>
                        <h2 className="text-xl font-bold text-[#F0F4F8]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{getCatDisplay('Advanced').name}</h2>
                        <span className="text-xs text-[#5C6778]">{allAdvanced.length} worker{allAdvanced.length !== 1 ? 's' : ''}</span>
                      </div>
                      <p className="text-sm text-[#5C6778] mb-7 ml-11">Advanced workers and specialist AI tools for power users</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {allAdvanced.map(t => <AutomationCard key={t.id || t.slug} template={t} onShare={handleShare} />)}
                      </div>
                    </div>
                  );
                })()}

                {mainFilteredTemplates.length === 0 && searchQuery && (
                  <div className="text-center py-20" data-testid="automations-empty-state">
                    <p className="text-[#7D8896] text-lg mb-2">No workers found</p>
                    <p className="text-[#5C6778] text-sm">Try adjusting your search or filters</p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1F2937] p-12">
              <h2 className="text-2xl font-bold text-[#F0F4F8] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Tell us what you want automated</h2>
              <p className="text-[#7D8896] text-base mb-8 max-w-md mx-auto">
                We'll build a system for your exact workflow.
              </p>
              <div className="flex items-center justify-center gap-4">
                <button data-testid="gallery-cta-create" onClick={() => navigate('/register')}
                  className="px-8 py-3 rounded-lg bg-[#00E5C0] hover:bg-[#00D4B0] text-[#12161C] text-sm font-bold transition-colors">
                  Create Your Own Worker
                </button>
                <button data-testid="gallery-cta-login" onClick={() => navigate('/login')}
                  className="px-8 py-3 rounded-lg border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] text-sm font-medium hover:text-[#F0F4F8] hover:border-[rgba(255,255,255,0.15)] transition-all">
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
